// 偵測區域繪製 UI (APP#45 / CAM-60) — 在即時預覽框上「8×4 grid 塗選」框出 include ROI，
// 選中的格由 mergeGridToRegions 併成 ≤8 個矩形，存 → DetectionZones raw op:set（見 zones.ts）。
// 底圖 (CAM-30)：進頁開即時 live view (useLiveSession，掛載開看/卸載 stop_viewing；
// 該 hook 只協商 recvonly video，天生 video-only、不碰音訊)。
// ⚠️ 座標對齊：區域座標是整幅畫面 0–100%，繪圖框長寬比綁「影格實際比例」(首幀 loadedmetadata
// 後由 videoWidth/Height 設定)，<video> 用 object-fit:contain；框比＝影格比 ⇒ 無 letterbox。
// grid 常數/演算法/golden 與 Android DetectionZones.kt、iOS DetectionZones.swift 三端鏡像。
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signaling } from '../../../lib/ws';
import { useLiveSession } from '../../live/useLiveSession';
import { MOTION_DETECTION_ZONES } from './ids';
import { opGet } from './ops';
import {
  clampRegion,
  encodeZoneList,
  MAX_ZONES,
  mergeGridToRegions,
  parseZoneList,
  ZONE_PRESETS,
  type ZoneRegion,
} from './zones';

type LoadState = 'loading' | 'set' | 'unset' | 'unavailable';

const ZONE_COLS = 8;
const ZONE_ROWS = 4;

/** 選中的格 index 集合 → rows×cols 布林（餵給 mergeGridToRegions）。 */
function cellsToGrid(cells: Set<number>): boolean[][] {
  const g: boolean[][] = Array.from({ length: ZONE_ROWS }, () => Array.from({ length: ZONE_COLS }, () => false));
  for (const idx of cells) {
    const r = Math.floor(idx / ZONE_COLS);
    const c = idx % ZONE_COLS;
    if (r >= 0 && r < ZONE_ROWS && c >= 0 && c < ZONE_COLS) g[r][c] = true;
  }
  return g;
}

/** 讀回的矩形集合 → 格 index（格中心落在任一矩形內即選中）。 */
function regionsToCells(regions: ZoneRegion[]): Set<number> {
  const out = new Set<number>();
  for (let r = 0; r < ZONE_ROWS; r++) {
    for (let c = 0; c < ZONE_COLS; c++) {
      const cx = ((c + 0.5) / ZONE_COLS) * 100;
      const cy = ((r + 0.5) / ZONE_ROWS) * 100;
      if (regions.some((g) => cx >= g.x && cx < g.x + g.xlen && cy >= g.y && cy < g.y + g.ylen)) out.add(r * ZONE_COLS + c);
    }
  }
  return out;
}

export function ZoneEditor({ deviceId, disabled }: { deviceId: string; disabled?: boolean }) {
  const { t } = useTranslation();
  // CAM-60：8×4 grid 塗選。cells 存選中的格 index (r*ZONE_COLS+c)。
  const [cells, setCells] = useState<Set<number>>(new Set());
  const [load, setLoad] = useState<LoadState>('loading');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const painting = useRef(false);
  const paintOn = useRef(true); // 拖曳當下是塗上(true)還是擦掉(false)，由起點格決定
  const alive = useRef(true);

  // CAM-30 即時底圖：掛載開看、卸載自動 stop_viewing。frameAspect 綁影格實際比例 → 無 letterbox。
  const videoRef = useRef<HTMLVideoElement>(null);
  const live = useLiveSession(deviceId, videoRef);
  const [frameAspect, setFrameAspect] = useState(16 / 9);
  const updateAspect = useCallback(() => {
    const v = videoRef.current;
    if (v && v.videoWidth > 0 && v.videoHeight > 0) setFrameAspect(v.videoWidth / v.videoHeight);
  }, []);

  // 選中的格 → 送出的矩形集合 (≤8)；> MAX_ZONES 時擋存。
  const regions = useMemo(() => mergeGridToRegions(cellsToGrid(cells), ZONE_COLS, ZONE_ROWS), [cells]);
  const tooMany = regions.length > MAX_ZONES;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // 讀回目前區域 (op:get)。空 / "0" / 非法 → unset；讀取失敗 → unavailable。
  useEffect(() => {
    let cancelled = false;
    opGet(deviceId, MOTION_DETECTION_ZONES).then(
      (f) => {
        if (cancelled || !alive.current) return;
        const rs = parseZoneList(f);
        if (rs.length > 0) {
          setCells(regionsToCells(rs));
          setLoad('set');
        } else {
          setLoad('unset');
        }
      },
      () => {
        if (!cancelled && alive.current) setLoad('unavailable');
      }
    );
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  /** 螢幕座標 → 格 index（超界夾回邊緣格）。 */
  const cellFromEvent = useCallback((e: React.PointerEvent): number | null => {
    const box = surfaceRef.current;
    if (!box) return null;
    const rect = box.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const fx = Math.min(0.999, Math.max(0, (e.clientX - rect.left) / rect.width));
    const fy = Math.min(0.999, Math.max(0, (e.clientY - rect.top) / rect.height));
    return Math.floor(fy * ZONE_ROWS) * ZONE_COLS + Math.floor(fx * ZONE_COLS);
  }, []);

  const applyCell = useCallback((idx: number) => {
    setCells((prev) => {
      const next = new Set(prev);
      if (paintOn.current) next.add(idx);
      else next.delete(idx);
      return next;
    });
    setSaved(false);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      const idx = cellFromEvent(e);
      if (idx == null) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      painting.current = true;
      paintOn.current = !cells.has(idx); // 起點沒選→塗上；有選→擦掉
      applyCell(idx);
    },
    [disabled, cellFromEvent, cells, applyCell]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (disabled || !painting.current) return;
      const idx = cellFromEvent(e);
      if (idx != null) applyCell(idx);
    },
    [disabled, cellFromEvent, applyCell]
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    painting.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  async function save() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await signaling.sendCmd(deviceId, 'set', MOTION_DETECTION_ZONES, { rawVal: encodeZoneList(regions) });
      if (!alive.current) return;
      setLoad('set');
      setSaved(true);
    } catch (err) {
      if (!alive.current) return;
      setSaveError(err instanceof Error ? err.message : t('deviceSettings.zoneSaveFailed'));
    } finally {
      if (alive.current) setSaving(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--spacing-3)', padding: 'var(--spacing-3) 0' }}>
      <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
        {t('deviceSettings.zoneGridHint')}
      </div>

      {/* 預覽框：底圖 = 即時 live；grid 疊層 + 塗選格 + 併出矩形粗框。框比綁影格比 → 無 letterbox。 */}
      <div
        ref={surfaceRef}
        role="application"
        aria-label={t('deviceSettings.detectionZone')}
        data-testid="zone-surface"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${frameAspect}`,
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-surface-line)',
          background: '#000',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          touchAction: 'none',
          overflow: 'hidden',
          opacity: disabled ? 'var(--opacity-disabled)' : 1,
        }}
      >
        {/* 即時底圖。pointerEvents:none → 塗選事件一律由外層 surface 接。muted 才能 autoplay。 */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          onLoadedMetadata={updateAspect}
          onResize={updateAspect}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
        />
        {/* 尚未串流：連線中 / 失敗 (可重試)。疊在影像之上、grid 之下。 */}
        {live.phase !== 'streaming' && (
          <div
            data-testid="zone-live-status"
            role="status"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              gap: 'var(--spacing-2)',
              color: 'var(--color-text-on-media, #fff)',
              fontSize: 'var(--font-size-sm)',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            {live.phase === 'failed' ? (
              <div style={{ display: 'grid', gap: 'var(--spacing-2)', placeItems: 'center', pointerEvents: 'auto' }}>
                <span>{live.error?.title ?? t('live.connectFailed')}</span>
                <button type="button" className="btn-ghost" data-testid="zone-live-retry" style={{ fontSize: 'var(--font-size-sm)' }} onClick={() => live.retry()}>
                  {t('common.retry')}
                </button>
              </div>
            ) : (
              <span>{t('live.phaseConnecting')}</span>
            )}
          </div>
        )}
        {/* grid 疊層（pointerEvents:none → 事件由外層接）：塗選格填色 + 格線 + 併出矩形粗框。 */}
        <div data-testid="zone-grid" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {Array.from(cells).map((idx) => {
            const r = Math.floor(idx / ZONE_COLS);
            const c = idx % ZONE_COLS;
            return (
              <div
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${(c / ZONE_COLS) * 100}%`,
                  top: `${(r / ZONE_ROWS) * 100}%`,
                  width: `${100 / ZONE_COLS}%`,
                  height: `${100 / ZONE_ROWS}%`,
                  background: 'color-mix(in srgb, var(--color-brand-primary) 28%, transparent)',
                }}
              />
            );
          })}
          {/* 格線 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(to right, rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.3) 1px, transparent 1px)',
              backgroundSize: `${100 / ZONE_COLS}% ${100 / ZONE_ROWS}%`,
            }}
          />
          {/* 併出的矩形（＝實際送出的區塊） */}
          {regions.map((g, i) => (
            <div
              key={`r${i}`}
              data-testid="zone-rect"
              style={{
                position: 'absolute',
                left: `${g.x}%`,
                top: `${g.y}%`,
                width: `${g.xlen}%`,
                height: `${g.ylen}%`,
                border: '2px solid var(--color-brand-primary)',
                boxSizing: 'border-box',
              }}
            />
          ))}
        </div>
      </div>

      {/* 讀回狀態 */}
      {load === 'unset' && (
        <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('deviceSettings.zoneUnset')}
        </p>
      )}
      {load === 'unavailable' && (
        <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('deviceSettings.zoneReadFailed')}
        </p>
      )}
      {/* 已用 X / 8 區塊；超過即擋存並提示簡化。 */}
      <p
        className={tooMany ? 'error-text' : 'text-secondary'}
        data-testid="zone-blocks"
        style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}
      >
        {t('deviceSettings.zoneBlocksUsed', { used: regions.length, max: MAX_ZONES })}
      </p>
      {tooMany && (
        <p className="error-text" role="alert" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('deviceSettings.zoneTooMany', { max: MAX_ZONES })}
        </p>
      )}

      {/* 常用區域 preset (8 個) → 填入對應格子。 */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
        {ZONE_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="btn-ghost"
            disabled={disabled}
            style={{ padding: 'var(--spacing-1) var(--spacing-4)', fontSize: 'var(--font-size-sm)' }}
            onClick={() => {
              setCells(regionsToCells([clampRegion(p.region)]));
              setSaved(false);
            }}
          >
            {t(`deviceSettings.zonePreset_${p.id}`)}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
        <button className="btn-primary" disabled={disabled || saving || tooMany} onClick={() => void save()}>
          {saving ? t('common.running') : t('deviceSettings.zoneSave')}
        </button>
        {saved && (
          <span className="text-secondary" role="status" style={{ fontSize: 'var(--font-size-sm)' }}>
            {t('deviceSettings.zoneSaved')}
          </span>
        )}
        {saveError && (
          <span className="error-text" role="alert" style={{ fontSize: 'var(--font-size-sm)' }}>
            {saveError}
          </span>
        )}
      </div>
    </div>
  );
}
